import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import './ServiceDetails.css';
import LanguageContext from './LanguageContext';

function TrainerDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useContext(LanguageContext);
  const [trainer, setTrainer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTrainerDetails();
  }, [id]);

  const translateTrainer = (trainer) => {
    if (!trainer) return trainer;
    
    const translated = { ...trainer };
    
    if (language === 'Arabic') {
      if (trainer.name_ar) translated.name = trainer.name_ar;
      if (trainer.title_ar) translated.title = trainer.title_ar;
      if (trainer.bio_ar) translated.bio = trainer.bio_ar;
      if (trainer.specialization_ar) translated.specialization = trainer.specialization_ar;
      if (trainer.certifications_ar) translated.certifications = trainer.certifications_ar;
    } else if (language === 'Russian') {
      if (trainer.name_ru) translated.name = trainer.name_ru;
      if (trainer.title_ru) translated.title = trainer.title_ru;
      if (trainer.bio_ru) translated.bio = trainer.bio_ru;
      if (trainer.specialization_ru) translated.specialization = trainer.specialization_ru;
      if (trainer.certifications_ru) translated.certifications = trainer.certifications_ru;
    } else if (language === 'German') {
      if (trainer.name_de) translated.name = trainer.name_de;
      if (trainer.title_de) translated.title = trainer.title_de;
      if (trainer.bio_de) translated.bio = trainer.bio_de;
      if (trainer.specialization_de) translated.specialization = trainer.specialization_de;
      if (trainer.certifications_de) translated.certifications = trainer.certifications_de;
    }
    
    return translated;
  };

  const fetchTrainerDetails = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const { data, error: fetchError } = await supabase
        .from('trainers')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      if (data) {
        setTrainer(data);
      } else {
        setError('Trainer not found');
      }
    } catch (err) {
      console.error('Error fetching trainer details:', err);
      setError('Failed to load trainer details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const translatedTrainer = trainer ? translateTrainer(trainer) : null;

  if (isLoading) {
    return (
      <div className="service-details-page">
        <div className="container" style={{ padding: '4rem 1rem', textAlign: 'center' }}>
          <div className="loading-spinner" style={{ 
            width: '50px', 
            height: '50px', 
            border: '4px solid rgba(124, 58, 237, 0.3)',
            borderTopColor: '#7C3AED',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <p>Loading trainer details...</p>
        </div>
      </div>
    );
  }

  if (error || !trainer) {
    return (
      <div className="service-details-page">
        <div className="container" style={{ padding: '4rem 1rem', textAlign: 'center' }}>
          <p style={{ color: '#ef4444', fontSize: '1.2rem' }}>
            {error || 'Trainer not found'}
          </p>
          <button 
            onClick={() => navigate('/trainings')}
            style={{
              marginTop: '1rem',
              padding: '0.75rem 1.5rem',
              background: '#7C3AED',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Back to Trainings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="service-details-page">
      {/* Trainer Header */}
      <section className="service-details-header">
        <div className="container">
          <div className="header-content">
            <h1 className="service-details-title">{translatedTrainer.name}</h1>
            {translatedTrainer.title && (
              <div className="service-type-badge-large">
                {translatedTrainer.title}
              </div>
            )}
          </div>
        </div>
        <div className="wave-divider-header"></div>
      </section>

      {/* Trainer Visual */}
      <section className="service-visual-section">
        <div className="container">
          <div className="service-image-container">
            <img 
              src={trainer.image || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1200&h=600&fit=crop&auto=format'} 
              alt={translatedTrainer.name}
              className="service-details-image"
            />
            <div className="image-glow"></div>
          </div>
        </div>
      </section>

      {/* Trainer Summary */}
      <section className="service-summary-section">
        <div className="container">
          <div className="summary-grid">
            {translatedTrainer.specialization && (
              <div className="summary-item">
                <div className="summary-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="currentColor"/>
                    <path d="M2 17L12 22L22 17V12L12 17L2 12V17Z" fill="currentColor"/>
                  </svg>
                </div>
                <div className="summary-content">
                  <span className="summary-label">Specialization</span>
                  <span className="summary-value">{translatedTrainer.specialization}</span>
                </div>
              </div>
            )}
            {trainer.experience_years && (
              <div className="summary-item">
                <div className="summary-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="currentColor"/>
                  </svg>
                </div>
                <div className="summary-content">
                  <span className="summary-label">Experience</span>
                  <span className="summary-value">{trainer.experience_years} years</span>
                </div>
              </div>
            )}
            {trainer.languages && Array.isArray(trainer.languages) && trainer.languages.length > 0 && (
              <div className="summary-item">
                <div className="summary-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12.87 15.07L10.33 12.56L10.36 12.53C12.1 10.59 13.34 8.36 14.07 6H17V4H10V2H8V4H1V6H12.17C11.5 7.92 10.44 9.75 9 11.35C8.07 10.32 7.3 9.19 6.69 8H4.69C5.42 9.63 6.42 11.17 7.67 12.56L2.58 17.58L4 19L9 14L11.5 16.5L12.87 15.07ZM18.5 10H16.5L12 22H14L15.12 19H19.88L21 22H23L18.5 10ZM15.88 17L17.5 12.67L19.12 17H15.88Z" fill="currentColor"/>
                  </svg>
                </div>
                <div className="summary-content">
                  <span className="summary-label">Languages</span>
                  <span className="summary-value">{trainer.languages.join(', ')}</span>
                </div>
              </div>
            )}
            {translatedTrainer.certifications && (
              <div className="summary-item">
                <div className="summary-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
                  </svg>
                </div>
                <div className="summary-content">
                  <span className="summary-label">Certifications</span>
                  <span className="summary-value">{translatedTrainer.certifications}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Full Bio */}
      <section className="description-section">
        <div className="container">
          <div className="description-content">
            <h2 className="section-title">About {translatedTrainer.name}</h2>
            <p className="service-description-full">{translatedTrainer.bio}</p>
          </div>
        </div>
      </section>

      {/* Audio Support Section */}
      {trainer.audio_url && (
        <section className="audio-section">
          <div className="container">
            <h2 className="section-title">Meditation Audio</h2>
            <div className="audio-player-container">
              <div className="audio-visual">
                <div className="waveform">
                  {[...Array(20)].map((_, i) => (
                    <div 
                      key={i} 
                      className="wave-bar"
                      style={{ animationDelay: `${i * 0.1}s` }}
                    ></div>
                  ))}
                </div>
              </div>
              <div className="audio-controls">
                <audio 
                  controls
                  src={trainer.audio_url}
                  style={{ width: '100%', maxWidth: '600px' }}
                >
                  Your browser does not support the audio element.
                </audio>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Contact Information */}
      {(trainer.email || trainer.phone) && (
        <section className="description-section">
          <div className="container">
            <div className="description-content">
              <h2 className="section-title">Contact Information</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {trainer.email && (
                  <p style={{ fontSize: '1.1rem', color: '#E0E7FF' }}>
                    <strong>Email:</strong> <a href={`mailto:${trainer.email}`} style={{ color: '#C084FC' }}>{trainer.email}</a>
                  </p>
                )}
                {trainer.phone && (
                  <p style={{ fontSize: '1.1rem', color: '#E0E7FF' }}>
                    <strong>Phone:</strong> <a href={`tel:${trainer.phone}`} style={{ color: '#C084FC' }}>{trainer.phone}</a>
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Gentle Closure */}
      <footer className="service-details-footer">
        <div className="wave-footer-closure"></div>
      </footer>
    </div>
  );
}

export default TrainerDetails;
